// Page de gestion des offres d'emploi - Espace Pro
// Publication et suivi des annonces

import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useOutletContext } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Briefcase, Plus, Search, Eye, Edit, Trash2, Pause, Play,
  Users, Calendar, MapPin, Euro, TrendingUp, CheckCircle2, X,
  Clock, AlertCircle, ChevronRight, ExternalLink, Copy, BarChart3
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { toast } from 'sonner';

import { API_URL } from '../../config/api';

// Statuts des offres
const JOB_STATUS = {
  active: { label: 'Active', color: 'bg-green-100 text-green-700', icon: CheckCircle2 },
  paused: { label: 'En pause', color: 'bg-yellow-100 text-yellow-700', icon: Pause },
  expired: { label: 'Expirée', color: 'bg-gray-100 text-gray-600', icon: Clock },
  closed: { label: 'Fermée', color: 'bg-red-100 text-red-600', icon: X },
};

// Types de contrat
const JOB_TYPES = {
  cdi: 'CDI',
  cdd: 'CDD',
  interim: 'Intérim',
  apprentissage: 'Apprentissage',
  stage: 'Stage',
  freelance: 'Freelance',
};

// Format date
const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleDateString('fr-FR', {
    day: '2-digit', month: 'short', year: 'numeric'
  });
};

// Carte d'offre
function JobCard({ job, onEdit, onDelete, onToggleStatus, onViewApplications }) {
  const status = JOB_STATUS[job.status] || JOB_STATUS.active;
  const StatusIcon = status.icon;

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white rounded-xl border border-gray-200 overflow-hidden hover:shadow-lg transition-shadow"
    >
      <div className="p-5">
        {/* Header */}
        <div className="flex items-start justify-between mb-4">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${status.color}`}>
                <StatusIcon size={12} />
                {status.label}
              </span>
              {job.is_urgent && (
                <span className="px-2 py-0.5 bg-red-100 text-red-700 text-xs font-bold rounded">
                  URGENT
                </span>
              )}
            </div>
            <h3 className="font-bold text-lg text-gray-900">{job.title}</h3>
          </div>
          <div className="flex items-center gap-1">
            {job.status === 'active' ? (
              <button
                onClick={() => onToggleStatus(job, 'paused')}
                className="p-2 hover:bg-gray-100 rounded-lg"
                title="Mettre en pause"
              >
                <Pause size={18} className="text-yellow-600" />
              </button>
            ) : job.status === 'paused' ? (
              <button
                onClick={() => onToggleStatus(job, 'active')}
                className="p-2 hover:bg-gray-100 rounded-lg"
                title="Réactiver"
              >
                <Play size={18} className="text-green-600" />
              </button>
            ) : null}
            <button
              onClick={() => onEdit(job)}
              className="p-2 hover:bg-gray-100 rounded-lg"
              title="Modifier"
            >
              <Edit size={18} className="text-gray-500" />
            </button>
            <button
              onClick={() => onDelete(job)}
              className="p-2 hover:bg-gray-100 rounded-lg"
              title="Supprimer"
            >
              <Trash2 size={18} className="text-red-500" />
            </button>
          </div>
        </div>

        {/* Info */}
        <div className="flex flex-wrap gap-3 mb-4 text-sm text-gray-600">
          <span className="flex items-center gap-1">
            <Briefcase size={14} className="text-gray-400" />
            {JOB_TYPES[job.job_type] || job.job_type}
          </span>
          <span className="flex items-center gap-1">
            <MapPin size={14} className="text-gray-400" />
            {job.city}
          </span>
          {job.salary_min && (
            <span className="flex items-center gap-1">
              <Euro size={14} className="text-gray-400" />
              {job.salary_min.toLocaleString()}
              {job.salary_max ? ` - ${job.salary_max.toLocaleString()}` : ''}
              €/{job.salary_type === 'annuel' ? 'an' : job.salary_type === 'horaire' ? 'h' : 'mois'}
            </span>
          )}
        </div>

        {/* Stats */}
        <div className="grid grid-cols-3 gap-3 mb-4">
          <div className="bg-blue-50 rounded-lg p-3 text-center">
            <p className="text-2xl font-bold text-blue-600">{job.views_count || 0}</p>
            <p className="text-xs text-blue-600/70">Vues</p>
          </div>
          <div className="bg-green-50 rounded-lg p-3 text-center">
            <p className="text-2xl font-bold text-green-600">{job.applications_count || 0}</p>
            <p className="text-xs text-green-600/70">Candidatures</p>
          </div>
          <div className="bg-purple-50 rounded-lg p-3 text-center">
            <p className="text-2xl font-bold text-purple-600">
              {job.applications_count && job.views_count 
                ? Math.round((job.applications_count / job.views_count) * 100) 
                : 0}%
            </p>
            <p className="text-xs text-purple-600/70">Conversion</p>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between pt-4 border-t border-gray-100">
          <span className="text-xs text-gray-500">
            Publiée le {formatDate(job.created_at)}
          </span>
          <div className="flex items-center gap-2">
            <button
              onClick={() => {
                const url = `${window.location.origin}/reseau/emploi?q=${encodeURIComponent(job.title)}`;
                navigator.clipboard.writeText(url);
                toast.success('Lien copié !');
              }}
              className="text-sm text-gray-500 hover:text-gray-700 flex items-center gap-1"
            >
              <Copy size={14} /> Partager
            </button>
            <Button
              size="sm"
              onClick={() => onViewApplications(job)}
              className="bg-[#2557a7] hover:bg-[#1a4a8f] rounded-lg"
            >
              <Users size={14} className="mr-1" />
              Candidatures
            </Button>
          </div>
        </div>
      </div>
    </motion.div>
  );
}

// Modal des candidatures
function ApplicationsModal({ job, onClose }) {
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const token = localStorage.getItem('buyer_token');

  useEffect(() => {
    const fetchApplications = async () => {
      try {
        const res = await fetch(`${API_URL}/api/ecosystem/jobs/${job.id}/applications`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (res.ok) {
          setApplications(await res.json());
        }
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    };
    fetchApplications();
  }, [job.id, token]);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        className="bg-white rounded-2xl max-w-2xl w-full max-h-[80vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-bold text-gray-900">Candidatures</h2>
              <p className="text-sm text-gray-500">{job.title}</p>
            </div>
            <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full">
              <X size={20} />
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="w-8 h-8 border-4 border-[#2557a7]/20 border-t-[#2557a7] rounded-full animate-spin" />
            </div>
          ) : applications.length === 0 ? (
            <div className="text-center py-12">
              <Users size={48} className="mx-auto text-gray-300 mb-4" />
              <h3 className="font-bold text-gray-900 mb-2">Aucune candidature</h3>
              <p className="text-gray-500">Tu n'as pas encore reçu de candidatures</p>
            </div>
          ) : (
            <div className="space-y-4">
              {applications.map((app) => (
                <div key={app.id} className="bg-gray-50 rounded-xl p-4">
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <h4 className="font-semibold text-gray-900">{app.applicant_name || 'Candidat'}</h4>
                      <p className="text-sm text-gray-500">{app.email}</p>
                      {app.phone && <p className="text-sm text-gray-500">{app.phone}</p>}
                    </div>
                    <span className="text-xs text-gray-400">
                      {formatDate(app.created_at)}
                    </span>
                  </div>
                  <div className="bg-white rounded-lg p-3 mb-3">
                    <p className="text-sm text-gray-600 whitespace-pre-line">{app.message}</p>
                  </div>
                  {app.cv_url && (
                    <a
                      href={app.cv_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm text-[#2557a7] hover:underline flex items-center gap-1"
                    >
                      <ExternalLink size={14} /> Voir le CV
                    </a>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </motion.div>
    </motion.div>
  );
}

// Page principale
export default function OffresEmploiPage() {
  const navigate = useNavigate();
  const { sellerProfile } = useOutletContext() || {};
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedJob, setSelectedJob] = useState(null);
  const [showApplications, setShowApplications] = useState(null);
  
  const token = localStorage.getItem('buyer_token');

  const fetchJobs = useCallback(async () => {
    try {
      setLoading(true);
      const res = await fetch(`${API_URL}/api/compte/jobs`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        setJobs(await res.json());
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchJobs();
  }, [fetchJobs]);

  const handleDelete = async (job) => {
    if (!window.confirm('Supprimer cette offre ?')) return;
    
    try {
      const res = await fetch(`${API_URL}/api/ecosystem/jobs/${job.id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        toast.success('Offre supprimée');
        fetchJobs();
      }
    } catch (e) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleToggleStatus = async (job, newStatus) => {
    try {
      const res = await fetch(`${API_URL}/api/ecosystem/jobs/${job.id}/status`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ status: newStatus })
      });
      if (res.ok) {
        toast.success(newStatus === 'active' ? 'Offre réactivée' : 'Offre mise en pause');
        fetchJobs();
      }
    } catch (e) {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const filteredJobs = jobs.filter(job => 
    job.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    job.city?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Stats globales
  const stats = {
    total: jobs.length,
    active: jobs.filter(j => j.status === 'active').length,
    totalViews: jobs.reduce((sum, j) => sum + (j.views_count || 0), 0),
    totalApplications: jobs.reduce((sum, j) => sum + (j.applications_count || 0), 0),
  };

  return (
    <div className="p-4 sm:p-6" data-testid="offres-emploi-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Mes offres d'emploi</h1>
          <p className="text-gray-500">Gère tes annonces et suis les candidatures</p>
        </div>
        <Button
          onClick={() => navigate('/reseau/emploi')}
          className="bg-[#2557a7] hover:bg-[#1a4a8f] rounded-xl"
        >
          <Plus size={18} className="mr-2" />
          Publier une offre
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
        <div className="bg-white rounded-xl p-4 border border-gray-200">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
              <Briefcase size={20} className="text-blue-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
              <p className="text-xs text-gray-500">Offres totales</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl p-4 border border-gray-200">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
              <CheckCircle2 size={20} className="text-green-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">{stats.active}</p>
              <p className="text-xs text-gray-500">Actives</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl p-4 border border-gray-200">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center">
              <Eye size={20} className="text-purple-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">{stats.totalViews}</p>
              <p className="text-xs text-gray-500">Vues totales</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl p-4 border border-gray-200">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-orange-100 flex items-center justify-center">
              <Users size={20} className="text-orange-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">{stats.totalApplications}</p>
              <p className="text-xs text-gray-500">Candidatures</p>
            </div>
          </div>
        </div>
      </div>

      {/* Search */}
      <div className="mb-6">
        <div className="relative max-w-md">
          <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <Input
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Rechercher une offre..."
            className="pl-10 rounded-xl"
          />
        </div>
      </div>

      {/* Liste */}
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <div className="w-10 h-10 border-4 border-[#2557a7]/20 border-t-[#2557a7] rounded-full animate-spin" />
        </div>
      ) : filteredJobs.length === 0 ? (
        <div className="bg-white rounded-2xl p-12 text-center border border-gray-200">
          <Briefcase size={48} className="mx-auto text-gray-300 mb-4" />
          <h3 className="font-bold text-gray-900 mb-2">
            {searchQuery ? 'Aucune offre trouvée' : 'Aucune offre publiée'}
          </h3>
          <p className="text-gray-500 mb-6">
            {searchQuery 
              ? 'Modifie ta recherche'
              : 'Publie ta première offre d\'emploi gratuitement'}
          </p>
          {!searchQuery && (
            <Button
              onClick={() => navigate('/reseau/emploi')}
              className="bg-[#2557a7] hover:bg-[#1a4a8f] rounded-xl"
            >
              <Plus size={18} className="mr-2" />
              Publier une offre
            </Button>
          )}
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 gap-4">
          {filteredJobs.map((job) => (
            <JobCard
              key={job.id}
              job={job}
              onEdit={() => navigate(`/reseau/emploi?edit=${job.id}`)}
              onDelete={handleDelete}
              onToggleStatus={handleToggleStatus}
              onViewApplications={(j) => setShowApplications(j)}
            />
          ))}
        </div>
      )}

      {/* Modal candidatures */}
      <AnimatePresence>
        {showApplications && (
          <ApplicationsModal
            job={showApplications}
            onClose={() => setShowApplications(null)}
          />
        )}
      </AnimatePresence>
    </div>
  );
}
